import {
  Component,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
/**
 * Reusable CAPTCHA component that renders a randomized image on a canvas and validates user input.
 * This component generates a random alphanumeric string, draws it on a canvas with per-character rotation and random colors, overlays noise lines, and verifies user-entered text against the generated value. It uses ViewChild to access the canvas and input elements, maintains a captchaError flag for UI feedback, and emits verification results to parent components.
 * Key methods:
 *   - generateCaptcha(): Generates a new captcha string, draws it to the canvas, and clears the input field.
 *   - generateRandomText(length = 6): Returns a random alphanumeric string of given length.
 *   - drawCaptcha(text): Renders the provided text to the canvas with random rotations, colors, and noise lines.
 *   - getRandomColor(): Produces a random hex color used for characters and noise.
 *   - verifyCaptcha(userInput: string): Compares user input to the current captcha (case-insensitive), updates captchaError, emits captchaStatus, and regenerates the captcha on failure.
 * Lifecycle hooks:
 *   - ngAfterViewInit(): Initializes the CanvasRenderingContext2D and generates the initial captcha after view initialization.
 * @component
 * @selector app-captcha
 * @example
 *   <app-captcha (captchaStatus)="onCaptchaStatus($event)"></app-captcha>
 * @input none none - This component does not declare any @Input properties; captcha generation and verification are managed internally.
 * @output EventEmitter<boolean> captchaStatus - Emits true when the provided input matches the displayed captcha, false when it does not; parents can subscribe to respond to verification results.
 */
@Component({
  selector: 'app-captcha',
  templateUrl: './captcha.component.html',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./captcha.component.scss'],
})
export class CaptchaComponent implements AfterViewInit {
  @ViewChild('canvas') canvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('captchaInput') captchaInput!: ElementRef<HTMLInputElement>;
  @Output() captchaStatus = new EventEmitter<boolean>();
  private ctx!: CanvasRenderingContext2D;
  private captchaText = '';
  public captchaError = false;

  constructor() {}

  ngAfterViewInit(): void {
    const canvasEl = this.canvas.nativeElement;
    const context = canvasEl.getContext('2d');
    if (context) {
      this.ctx = context;
      this.generateCaptcha();
    }
  }

  generateCaptcha() {
    this.captchaText = this.generateRandomText();
    this.drawCaptcha(this.captchaText);
    // Clear the input field when generating new captcha
    if (this.captchaInput?.nativeElement) {
      this.captchaInput.nativeElement.value = '';
    }
  }

  private generateRandomText(length = 6): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i += 1) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Draws the provided text onto the component's canvas as a styled CAPTCHA image.
   * @example
   * this.drawCaptcha('aB3dE')
   * undefined
   * @param {{string}} {{text}} - The text to render as the CAPTCHA.
   * @returns {{void}} No return value; the function draws directly to the component's canvas.
   **/
  private drawCaptcha(text: string) {
    if (!this.ctx) {
      return;
    }
    this.ctx.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
    this.ctx.font = '24px Arial';
    this.ctx.fillStyle = '#ffffff';
    this.ctx.fillRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);

    for (let i = 0; i < text.length; i++) {
      this.ctx.save();
      this.ctx.translate(20 * i + 10, 30);
      this.ctx.rotate((Math.random() - 0.5) * 0.4);
      this.ctx.fillStyle = this.getRandomColor();
      this.ctx.fillText(text[i], 0, 0);
      this.ctx.restore();
    }

    for (let i = 0; i < 5; i += 1) {
      this.ctx.beginPath();
      this.ctx.moveTo(Math.random() * 120, Math.random() * 40);
      this.ctx.lineTo(Math.random() * 120, Math.random() * 40);
      this.ctx.strokeStyle = this.getRandomColor();
      this.ctx.stroke();
    }
  }

  private getRandomColor(): string {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i += 1) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  /**
   * Validate captcha input case-insensitively, update component state, emit status, and regenerate on failure.
   * @example
   * erifyCaptcha('userInput')
   * true
   * @param {{string}} {{userInput}} - The user's captcha input to validate.
   * @returns {{boolean}} Whether the provided input matches the current captcha text (true if match, false otherwise).
   **/
  verifyCaptcha(userInput: string): boolean {
    if (userInput.toLowerCase() === this.captchaText.toLowerCase()) {
      this.captchaError = false;
      this.captchaStatus.emit(true);
      return true;
    }
    this.captchaError = true;
    this.captchaStatus.emit(false);
    this.generateCaptcha();
    return false;
  }
}
